// navPage.tsx
import React, { useState, useEffect } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { GiHamburger } from "react-icons/gi";
import { BsFillPersonFill, BsStack } from "react-icons/bs";
import { RiPagesFill, RiContactsBookFill, RiLightbulbFlashFill, RiLightbulbFlashLine } from "react-icons/ri";
import { FaCalendarAlt } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import "../App.scss";
import AboutMe from "./aboutMe";
import Projects from "./projects";
import Resume from "./resume";
import Contact from "./contact";
import { useTheme } from "../ThemeContext";
import Sidebar from "./sidebar";
import Footer from "./footer";

type VisibleComponent = "home" | "stack" | "about" | "contact";

export default function NavPage({ initialView = "home" }: { initialView?: VisibleComponent }) {
  const navigate = useNavigate();
  const [visible, setVisible] = useState<VisibleComponent>(initialView);
  const themeContext = useTheme();

  if (!themeContext) {
    console.error('ThemeContext not available');
    return null; // or your fallback UI here
  }

  const { theme, toggleTheme } = themeContext;
  
  const [isDark, updateIsDark] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isSidebarVisible, setIsSidebarVisible] = useState(false);

  useEffect(() => {
    if (theme === 'dark') {
      updateIsDark(true);
      document.documentElement.classList.add('dark-theme');
    } else {
      updateIsDark(false);
      document.documentElement.classList.remove('dark-theme');
    }

    // Handle window resize
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    // Add event listener for window resize
    window.addEventListener('resize', handleResize);

    // Cleanup event listener on component unmount
    return () => window.removeEventListener('resize', handleResize);
  }, [theme]);

  const changeVisible = (newVisible: VisibleComponent) => {
    setVisible(newVisible);
    setIsSidebarVisible(false); // Close sidebar when an item is selected
  };

  const toggleSidebar = () => {
    setIsSidebarVisible(!isSidebarVisible);
  };

  const subViewVariants = {
    initial: { opacity: 0, y: 20 },
    animate: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.35, ease: "easeOut" },
    },
    exit: {
      opacity: 0,
      y: -20,
      transition: { duration: 0.25, ease: "easeIn" },
    },
  };

  const renderComponent = () => {
    switch (visible) {
      case "contact":
        return <Contact />;
      case "stack":
        return <Projects />;
      case "about":
        return <Resume />;
      default:
        return <AboutMe />;
    }
  };

  return (
    <>
      <Container fluid className="p-0 d-flex flex-column align-items-stretch" style={{ overflow: "auto", minHeight: "100vh" }}>
        <Row className="m-0">
          <Col className="d-flex">
            <AnimatePresence exitBeforeEnter>
              <motion.div
                key={visible}
                variants={subViewVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                style={{ width: "100%" }}
              >
                {renderComponent()}
              </motion.div>
            </AnimatePresence>
          </Col>
        </Row>
        <Footer />
      </Container>
      <button
        className="toggle-theme-btn"
        onClick={toggleTheme}
        aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}
        style={{
          position: "fixed",
          top: "20px",
          right: "20px",
          zIndex: 9999,
        }}
      >
        {isDark ? <RiLightbulbFlashFill className="icon"/> : <RiLightbulbFlashLine className="icon"/>}
      </button>
      
      {isMobile && (
        <>
          <button
            className="toggle-theme-btn"
            onClick={toggleSidebar}
            aria-label={isSidebarVisible ? 'Close navigation' : 'Open navigation'}
            aria-expanded={isSidebarVisible}
            aria-controls="portfolio-navigation"
            style={{
              position: "fixed",
              top: "20px",
              left: "20px",
              zIndex: 9999,
            }}
          >
            <GiHamburger className="icon"/>
          </button>
          <Sidebar isVisible={isSidebarVisible} changeVisible={changeVisible} />
        </>
      )}

      {!isMobile && (
        <nav className="portfolio-dock d-flex justify-content-between align-items-center fixed-bottom pb-5 px-3" aria-label="Portfolio navigation">
          <button className="navButton" aria-label="Home" title="Home" onClick={() => changeVisible("home")}>
            <BsFillPersonFill className="icon" />
          </button>

          <button className="navButton" aria-label="Projects" title="Projects" onClick={() => changeVisible("stack")}>
            <BsStack className="icon" />
          </button>

          <button className="navButton" aria-label="About" title="Work experience" onClick={() => changeVisible("about")}>
            <RiPagesFill className="icon" />
          </button>

          <button className="navButton" aria-label="Contact" title="Contact" onClick={() => changeVisible("contact")}>
            <RiContactsBookFill className="icon" />
          </button>

          <button className="navButton schedule-nav-button" aria-label="Schedule a meeting" title="Schedule a meeting" onClick={() => navigate("/calendar")}>
            <FaCalendarAlt className="icon" />
          </button>
        </nav>
      )}
    </>
  );
}
